import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  RefreshCw,
  XCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
  BarChart3,
  AlertCircle,
  Check,
  X,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { isAbortError } from '../../lib/supabase';
import { getValidAuthHeadersOrThrow } from '../../lib/auth';

interface SyncStatistics {
  total_mapped_products: number;
  verified_products: number;
  unverified_products: number;
  products_with_mismatch: number;
  open_discrepancies: number;
  critical_discrepancies: number;
  last_24h_transactions: number;
  last_24h_success_rate: number;
  last_successful_sync: string | null;
  pending_queue_items: number;
}

interface SyncTransaction {
  id: string;
  transaction_type: string;
  status: string;
  items_requested: number;
  items_processed: number;
  items_verified: number;
  items_failed: number;
  items_with_discrepancy: number;
  error_summary: string | null;
  started_at: string;
  completed_at: string | null;
  duration_seconds: number;
}

interface Discrepancy {
  id: string;
  product_id: string;
  product_name: string;
  product_sku: string | null;
  local_quantity: number;
  shopify_quantity: number;
  discrepancy_amount: number;
  discrepancy_type: string;
  severity: string;
  detected_by: string;
  created_at: string;
}

interface ReconciliationResult {
  runId: string;
  status: string;
  summary: {
    totalChecked: number;
    matched: number;
    mismatched: number;
    errors: number;
    autoFixed: number;
  };
  duration: number;
}

export const SyncStatusDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<SyncStatistics | null>(null);
  const [transactions, setTransactions] = useState<SyncTransaction[]>([]);
  const [discrepancies, setDiscrepancies] = useState<Discrepancy[]>([]);
  const [showTransactions, setShowTransactions] = useState(false);
  const [showDiscrepancies, setShowDiscrepancies] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState<ReconciliationResult | null>(null);

  const [statsError, setStatsError] = useState<string | null>(null);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);
  const [discrepanciesError, setDiscrepanciesError] = useState<string | null>(null);
  const [reconcileError, setReconcileError] = useState<string | null>(null);

  const inflightRef = useRef<AbortController | null>(null);

  const baseUrl = import.meta.env.VITE_SUPABASE_URL;

  const fetchStatistics = useCallback(async (signal: AbortSignal) => {
    try {
      const response = await fetch(
        `${baseUrl}/functions/v1/shopify-push-inventory/statistics`,
        { headers: await getValidAuthHeadersOrThrow(), signal }
      );
      if (!response.ok) throw new Error(`Statistics fetch failed: ${response.status}`);
      const data = await response.json();
      setStats(data);
      setStatsError(null);
    } catch (err) {
      if (isAbortError(err)) return;
      setStatsError(err instanceof Error ? err.message : 'Failed to load sync statistics');
    }
  }, [baseUrl]);

  const fetchTransactions = useCallback(async (signal: AbortSignal) => {
    try {
      const response = await fetch(
        `${baseUrl}/functions/v1/shopify-push-inventory/transactions?limit=10`,
        { headers: await getValidAuthHeadersOrThrow(), signal }
      );
      if (!response.ok) throw new Error(`Transactions fetch failed: ${response.status}`);
      const data = await response.json();
      setTransactions(data.transactions || []);
      setTransactionsError(null);
    } catch (err) {
      if (isAbortError(err)) return;
      setTransactionsError(err instanceof Error ? err.message : 'Failed to load sync transactions');
    }
  }, [baseUrl]);

  const fetchDiscrepancies = useCallback(async (signal: AbortSignal) => {
    try {
      const response = await fetch(
        `${baseUrl}/functions/v1/shopify-push-inventory/discrepancies?limit=20`,
        { headers: await getValidAuthHeadersOrThrow(), signal }
      );
      if (!response.ok) throw new Error(`Discrepancies fetch failed: ${response.status}`);
      const data = await response.json();
      setDiscrepancies(data.discrepancies || []);
      setDiscrepanciesError(null);
    } catch (err) {
      if (isAbortError(err)) return;
      setDiscrepanciesError(err instanceof Error ? err.message : 'Failed to load discrepancies');
    }
  }, [baseUrl]);

  const loadAllData = useCallback(async () => {
    if (inflightRef.current) {
      inflightRef.current.abort();
    }
    const controller = new AbortController();
    inflightRef.current = controller;

    setLoading(true);
    setStatsError(null);
    setTransactionsError(null);
    setDiscrepanciesError(null);

    await Promise.all([
      fetchStatistics(controller.signal),
      fetchTransactions(controller.signal),
      fetchDiscrepancies(controller.signal),
    ]);

    if (!controller.signal.aborted) {
      setLoading(false);
      inflightRef.current = null;
    }
  }, [fetchStatistics, fetchTransactions, fetchDiscrepancies]);

  useEffect(() => {
    loadAllData();
    return () => {
      inflightRef.current?.abort();
    };
  }, [loadAllData]);

  const handleReconcile = async (autoFix: boolean = false) => {
    setReconciling(true);
    setReconcileError(null);
    setReconcileResult(null);

    try {
      const response = await fetch(
        `${baseUrl}/functions/v1/shopify-reconcile`,
        {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({ autoFix, autoFixThreshold: 5 }),
          signal: AbortSignal.timeout(60000),
        }
      );

      let data;
      try {
        data = await response.json();
      } catch {
        if (response.status === 504) {
          throw new Error('Reconciliation timed out. Try again with fewer products or contact support.');
        }
        throw new Error(`Server returned ${response.status}. Please try again.`);
      }

      if (!response.ok) {
        throw new Error(data.error || 'Reconciliation failed');
      }

      setReconcileResult(data);
      await loadAllData();
    } catch (err) {
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        setReconcileError('Reconciliation request timed out. The server may still be processing. Try refreshing in a minute.');
      } else {
        setReconcileError(err instanceof Error ? err.message : 'Reconciliation failed');
      }
    } finally {
      setReconciling(false);
    }
  };

  const handleResolveDiscrepancy = async (discrepancyId: string, action: string) => {
    setDiscrepanciesError(null);
    try {
      const response = await fetch(
        `${baseUrl}/functions/v1/shopify-push-inventory/resolve-discrepancy`,
        {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({ discrepancyId, action }),
          signal: AbortSignal.timeout(30000),
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error((errData as Record<string, string>).error || `Failed to resolve: ${response.status}`);
      }

      const controller = new AbortController();
      await Promise.all([
        fetchDiscrepancies(controller.signal),
        fetchStatistics(controller.signal),
      ]);
    } catch (err) {
      if (isAbortError(err)) return;
      setDiscrepanciesError(err instanceof Error ? err.message : 'Failed to resolve discrepancy. Please try again.');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'failed': return 'text-red-600 bg-red-50';
      case 'partial': return 'text-amber-600 bg-amber-50';
      case 'processing': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-700 bg-red-100 border-red-200';
      case 'high': return 'text-orange-700 bg-orange-100 border-orange-200';
      case 'medium': return 'text-amber-700 bg-amber-100 border-amber-200';
      default: return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Sync Health Dashboard
        </h3>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => loadAllData()}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {statsError && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            Statistics unavailable: {statsError}
          </div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <Activity className="w-4 h-4" />
              Mapped Products
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.total_mapped_products}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {stats.verified_products} verified
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              24h Success Rate
            </div>
            <div className={`text-2xl font-bold ${
              stats.last_24h_success_rate >= 95 ? 'text-green-600' :
              stats.last_24h_success_rate >= 80 ? 'text-amber-600' : 'text-red-600'
            }`}>
              {stats.last_24h_success_rate}%
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {stats.last_24h_transactions} transactions
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Open Discrepancies
            </div>
            <div className={`text-2xl font-bold ${
              stats.open_discrepancies === 0 ? 'text-green-600' :
              stats.critical_discrepancies > 0 ? 'text-red-600' : 'text-amber-600'
            }`}>
              {stats.open_discrepancies}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {stats.critical_discrepancies} critical
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <Clock className="w-4 h-4" />
              Last Sync
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {stats.last_successful_sync ? formatDate(stats.last_successful_sync) : 'Never'}
            </div>
            {stats.pending_queue_items > 0 && (
              <div className="text-xs text-amber-600 mt-1">
                {stats.pending_queue_items} pending
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-gray-900 dark:text-white">Reconciliation</h4>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleReconcile(false)}
              disabled={reconciling}
            >
              {reconciling ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Check Inventory
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleReconcile(true)}
              disabled={reconciling}
            >
              {reconciling ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Auto-Fix Small Discrepancies
            </Button>
          </div>
        </div>

        {reconcileError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertCircle className="w-4 h-4" />
              {reconcileError}
            </div>
          </div>
        )}

        {reconcileResult && (
          <div className={`p-4 rounded-lg ${
            reconcileResult.summary.mismatched === 0 && reconcileResult.summary.errors === 0
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
              : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              {reconcileResult.summary.mismatched === 0 && reconcileResult.summary.errors === 0 ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              )}
              <span className="font-medium">
                Reconciliation {reconcileResult.status}
              </span>
              <span className="text-sm text-gray-500">
                ({(reconcileResult.duration / 1000).toFixed(1)}s)
              </span>
            </div>
            <div className="grid grid-cols-5 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Checked:</span>{' '}
                <span className="font-medium">{reconcileResult.summary.totalChecked}</span>
              </div>
              <div>
                <span className="text-gray-500">Matched:</span>{' '}
                <span className="font-medium text-green-600">{reconcileResult.summary.matched}</span>
              </div>
              <div>
                <span className="text-gray-500">Mismatched:</span>{' '}
                <span className="font-medium text-amber-600">{reconcileResult.summary.mismatched}</span>
              </div>
              <div>
                <span className="text-gray-500">Errors:</span>{' '}
                <span className="font-medium text-red-600">{reconcileResult.summary.errors}</span>
              </div>
              <div>
                <span className="text-gray-500">Auto-Fixed:</span>{' '}
                <span className="font-medium text-blue-600">{reconcileResult.summary.autoFixed}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setShowTransactions(!showTransactions)}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Recent Sync Transactions
            <span className="text-sm font-normal text-gray-500">
              ({transactions.length})
            </span>
          </h4>
          {showTransactions ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {transactionsError && (
          <div className="px-4 pb-3">
            <div className="p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              {transactionsError}
            </div>
          </div>
        )}

        {showTransactions && (
          <div className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
            {transactions.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No recent transactions
              </div>
            ) : (
              transactions.map((tx) => (
                <div key={tx.id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(tx.status)}`}>
                        {tx.status}
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {tx.transaction_type.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {formatDate(tx.started_at)}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <span>
                      <Check className="w-3 h-3 inline mr-1 text-green-500" />
                      {tx.items_verified} verified
                    </span>
                    {tx.items_with_discrepancy > 0 && (
                      <span>
                        <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-500" />
                        {tx.items_with_discrepancy} mismatch
                      </span>
                    )}
                    {tx.items_failed > 0 && (
                      <span>
                        <X className="w-3 h-3 inline mr-1 text-red-500" />
                        {tx.items_failed} failed
                      </span>
                    )}
                    {tx.duration_seconds > 0 && (
                      <span className="text-gray-400">
                        {tx.duration_seconds}s
                      </span>
                    )}
                  </div>
                  {tx.error_summary && (
                    <div className="mt-2 text-xs text-red-600 dark:text-red-400 truncate">
                      {tx.error_summary}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {(discrepancies.length > 0 || discrepanciesError) && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowDiscrepancies(!showDiscrepancies)}
            className="w-full flex items-center justify-between p-4 text-left"
          >
            <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Open Discrepancies
              {discrepancies.length > 0 && (
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  discrepancies.some(d => d.severity === 'critical')
                    ? 'bg-red-100 text-red-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {discrepancies.length}
                </span>
              )}
            </h4>
            {showDiscrepancies ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {discrepanciesError && (
            <div className="px-4 pb-3">
              <div className="p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                {discrepanciesError}
              </div>
            </div>
          )}

          {showDiscrepancies && discrepancies.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Product</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Local</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Shopify</th>
                      <th className="px-4 py-2 text-center font-medium text-gray-500">Diff</th>
                      <th className="px-4 py-2 text-center font-medium text-gray-500">Severity</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {discrepancies.map((d) => (
                      <tr key={d.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {d.product_name}
                          </div>
                          {d.product_sku && (
                            <div className="text-xs text-gray-500">SKU: {d.product_sku}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {d.local_quantity}
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {d.shopify_quantity}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-medium ${
                            d.discrepancy_type === 'local_higher' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {d.discrepancy_type === 'local_higher' ? '+' : '-'}
                            {d.discrepancy_amount}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getSeverityColor(d.severity)}`}>
                            {d.severity}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleResolveDiscrepancy(d.id, 'push_local')}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              title="Push local quantity to Shopify"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleResolveDiscrepancy(d.id, 'ignore')}
                              className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                              title="Ignore this discrepancy"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
